import { useThemeColor, Divider, Text } from '@wistia/vhs';
import type { WistiaPlayer } from '@wistia/wistia-player';
import { WistiaPlayerProvider } from '~/hooks/player/useWistiaPlayer';
import type { DefaultTheme } from 'styled-components';
import styled from 'styled-components';
import type { ApolloQueryResult } from '@apollo/client';
import { Waypoint } from 'react-waypoint';
import type { JSX } from 'react';
import { useCallback } from 'react';
import { isNil } from '~/utilities/type-guards';
import { CommentContextProvider, commentingViews } from '../Media/Comments/CommentContext';
import { CommentAndReplies } from '../Media/Comments/CommentsAndReplies';
import { AnonymousCommentForm } from '../Media/Comments/Forms/AnonymousCommentForm';
import { EmptyState } from '../Media/Comments/EmptyState';
import type { AnonymousMediaQuery } from './__gql/AnonymousMedia.gql.gen';
import { MoreAnonymousTeamCommentsDocument } from './__gql/AnonymousMedia.gql.gen';
import type { MediaPreviewQuery } from '../MediaPreview/__gql/MediaPreview.gql.gen';
import type { TopLevelComment } from '../Media/Comments/types';

const TeamCommentsContainer = styled.div<{ theme: DefaultTheme }>`
  background-color: white;
  border-left: ${({ theme }) => `1px solid ${theme.color.grey300}`};
  display: flex;
  flex-direction: column;
  flex-grow: 0;
  height: 100vh;
  justify-content: space-between;
  width: 383px;
`;

const CommentsHeading = styled(Text)`
  flex-grow: 0;
  margin: 16px auto 8px 16px;
`;

const CommentContent = styled.div`
  flex-grow: 1;
  overflow-y: auto;
  padding: 24px;
`;

export const TeamCommentsSidePanel = ({
  commentingDisabled,
  mediaId,
  mediaHashedId,
  videoEmbed,
  teamComments,
  fetchMore,
}: {
  commentingDisabled: boolean;
  mediaId: string;
  mediaHashedId: string;
  videoEmbed: WistiaPlayer | null;
  teamComments:
    | NonNullable<AnonymousMediaQuery['anonymousMedia']>['topLevelTeamComments']
    | NonNullable<MediaPreviewQuery['media']>['topLevelTeamComments'];
  fetchMore: (arg: GenericRecord) => Promise<ApolloQueryResult<AnonymousMediaQuery>>;
}): JSX.Element => {
  const grey100 = useThemeColor('grey100');

  const renderDivider = () => (
    <Divider
      variant="solid"
      height={4}
      colorOverride={grey100}
    />
  );

  const onCommentClick = useCallback(
    (commentId: string | null, timestamp?: number) => {
      if (isNil(commentId) || isNil(timestamp) || isNil(videoEmbed)) {
        return;
      }
      // eslint-disable-next-line no-param-reassign
      videoEmbed.currentTime = timestamp;
    },
    [videoEmbed],
  );

  const { pageInfo = {}, edges = [] } = teamComments as {
    pageInfo: { endCursor: string | null | undefined; hasNextPage: boolean };
    edges: { node: TopLevelComment }[];
  };
  const { endCursor, hasNextPage } = pageInfo as {
    endCursor: string | null | undefined;
    hasNextPage: boolean;
  };

  const loadMoreComments = () => {
    void fetchMore({
      query: MoreAnonymousTeamCommentsDocument,
      variables: { cursor: endCursor, mediaHashedId },
    });
  };

  const comments = edges.map((edge: { node: TopLevelComment }) => edge.node);

  return (
    <CommentContextProvider
      isAnonymousMedia={true}
      defaultCommentingView={commentingViews.team}
      onCommentClick={onCommentClick}
      showCommentTabs={false}
    >
      <WistiaPlayerProvider player={videoEmbed}>
        <TeamCommentsContainer>
          <CommentsHeading variant="subtitle1">Comments</CommentsHeading>
          {renderDivider()}
          <CommentContent>
            {comments.length === 0 && <EmptyState />}
            {comments.map((comment) => {
              return (
                <CommentAndReplies
                  key={comment.id}
                  topLevelComment={comment}
                  contact={null}
                  mediaId={mediaId}
                  isRightOfPlayer={true}
                />
              );
            })}
            {hasNextPage ? (
              <Waypoint
                bottomOffset="-50px"
                onEnter={loadMoreComments}
              />
            ) : null}
          </CommentContent>
          {renderDivider()}
          <AnonymousCommentForm
            commentingDisabled={commentingDisabled}
            mediaId={mediaId}
          />
        </TeamCommentsContainer>
      </WistiaPlayerProvider>
    </CommentContextProvider>
  );
};
